/**
 * Utilitaires de gestion des fuseaux horaires
 * Utilise date-fns-tz pour formater les dates dans le timezone de l'événement
 *
 * PRD : docs/PRDs/PRD-gestion-fuseaux-horaires.md
 */

import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import { enUS } from 'date-fns/locale';
import tzlookup from 'tz-lookup';

/**
 * Formate une date UTC dans le timezone de l'événement
 * @param datetime - Date ISO 8601 UTC ou objet Date
 * @param timezone - IANA timezone (ex: 'Asia/Colombo', 'Europe/Paris')
 * @param format - Format de sortie (défaut: 'HH:mm')
 */
export function formatEventTime(
  datetime: string | Date | undefined,
  timezone: string,
  format: string = 'HH:mm'
): string {
  if (!datetime) return '';
  try {
    const dateValue = datetime instanceof Date ? datetime.toISOString() : datetime;
    return formatInTimeZone(dateValue, timezone, format, { locale: enUS });
  } catch (error) {
    console.warn('formatEventTime error:', error);
    return '';
  }
}

/**
 * Formate la date complète dans le timezone de l'événement
 * @param datetime - Date ISO 8601 UTC ou objet Date
 * @param timezone - IANA timezone
 * @param format - Format de sortie (défaut: 'EEEE d MMMM yyyy')
 */
export function formatEventDate(
  datetime: string | Date | undefined,
  timezone: string,
  format: string = 'EEEE d MMMM yyyy'
): string {
  if (!datetime) return '';
  try {
    const dateValue = datetime instanceof Date ? datetime.toISOString() : datetime;
    return formatInTimeZone(dateValue, timezone, format, { locale: enUS });
  } catch (error) {
    console.warn('formatEventDate error:', error);
    return '';
  }
}

/**
 * Formate date et heure ensemble pour l'affichage
 * @param datetime - Date ISO 8601 UTC ou objet Date
 * @param timezone - IANA timezone
 */
export function formatEventDateTime(
  datetime: string | Date | undefined,
  timezone: string
): { date: string; time: string; weekday: string } {
  if (!datetime) return { date: '', time: '', weekday: '' };

  try {
    const dateValue = datetime instanceof Date ? datetime.toISOString() : datetime;
    return {
      date: formatInTimeZone(dateValue, timezone, 'd MMMM yyyy', { locale: enUS }),
      time: formatInTimeZone(dateValue, timezone, 'HH:mm', { locale: enUS }),
      weekday: formatInTimeZone(dateValue, timezone, 'EEEE', { locale: enUS }),
    };
  } catch (error) {
    console.warn('formatEventDateTime error:', error);
    return { date: '', time: '', weekday: '' };
  }
}

/**
 * Formate la date pour l'affichage dans l'icône (mois abrégé + jour)
 * @param datetime - Date ISO 8601 UTC ou objet Date
 * @param timezone - IANA timezone
 */
export function formatEventDateForIcon(
  datetime: string | Date | undefined,
  timezone: string
): { month: string; day: string } {
  if (!datetime) return { month: '', day: '' };

  try {
    const dateValue = datetime instanceof Date ? datetime.toISOString() : datetime;
    return {
      month: formatInTimeZone(dateValue, timezone, 'MMM', { locale: enUS }).toUpperCase(),
      day: formatInTimeZone(dateValue, timezone, 'd', { locale: enUS }),
    };
  } catch (error) {
    console.warn('formatEventDateForIcon error:', error);
    return { month: '', day: '' };
  }
}

/**
 * Convertit une date locale (formulaire) en UTC pour stockage
 * IMPORTANT: Utilise fromZonedTime (anciennement zonedTimeToUtc)
 *
 * @param localDate - Date locale au format "YYYY-MM-DD"
 * @param localTime - Heure locale au format "HH:mm"
 * @param timezone - IANA timezone de l'événement
 * @returns Date ISO 8601 UTC
 */
export function localToUTC(
  localDate: string,  // "2025-12-09"
  localTime: string,  // "09:00" ou "09:00:00"
  timezone: string    // "Asia/Colombo"
): string {
  // Normaliser le format de l'heure - s'assurer qu'on a "HH:mm:ss"
  let normalizedTime = localTime;
  const timeParts = localTime.split(':');
  if (timeParts.length === 2) {
    // Format "HH:mm" - ajouter les secondes
    normalizedTime = `${localTime}:00`;
  }
  // Si déjà 3 parties (HH:mm:ss), on garde tel quel

  const localDateTime = `${localDate}T${normalizedTime}`;
  // fromZonedTime convertit une heure locale vers UTC
  const utcDate = fromZonedTime(localDateTime, timezone);
  return utcDate.toISOString();
}

/**
 * Détecte le timezone depuis les coordonnées via tz-lookup (calcul local)
 * Pas d'appel API externe, 100% fiable et instantané
 *
 * @param latitude - Latitude du lieu
 * @param longitude - Longitude du lieu
 * @returns IANA timezone (fallback: 'Asia/Colombo')
 */
export function getTimezoneFromCoords(
  latitude: number,
  longitude: number
): string {
  const DEFAULT_TIMEZONE = 'Asia/Colombo';

  try {
    const timezone = tzlookup(latitude, longitude);
    console.log(`🌍 [Timezone] Détecté: ${timezone} pour (${latitude}, ${longitude})`);
    return timezone;
  } catch (error) {
    console.warn('🌍 [Timezone] Erreur détection, fallback:', DEFAULT_TIMEZONE, error);
    return DEFAULT_TIMEZONE;
  }
}

/**
 * Liste des timezones courants pour le sélecteur (fallback manuel)
 */
export const COMMON_TIMEZONES = [
  { value: 'Asia/Colombo', label: 'Sri Lanka (UTC+5:30)' },
  { value: 'Europe/Paris', label: 'France (UTC+1/+2)' },
  { value: 'Europe/London', label: 'Royaume-Uni (UTC+0/+1)' },
  { value: 'America/New_York', label: 'New York (UTC-5/-4)' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (UTC-8/-7)' },
  { value: 'Asia/Tokyo', label: 'Japon (UTC+9)' },
  { value: 'Australia/Sydney', label: 'Sydney (UTC+10/+11)' },
  { value: 'Asia/Dubai', label: 'Dubai (UTC+4)' },
  { value: 'Asia/Singapore', label: 'Singapour (UTC+8)' },
  { value: 'Asia/Bangkok', label: 'Bangkok (UTC+7)' },
] as const;

export type CommonTimezone = typeof COMMON_TIMEZONES[number]['value'];

/**
 * Retourne le label lisible d'un timezone
 */
export function getTimezoneLabel(timezone: string): string {
  const found = COMMON_TIMEZONES.find(tz => tz.value === timezone);
  return found?.label || timezone;
}

/**
 * Retourne le timezone de l'utilisateur (navigateur)
 */
/**
 * Convertit un endTime local en UTC en tenant compte du timezone de l'événement.
 * Gère le cas où l'heure de fin dépasse minuit (ex: start 18:00, end 00:30 → lendemain).
 * Fallback: si pas d'endTime, retourne startUTC + 2h.
 *
 * @param dateStr - Date locale au format "YYYY-MM-DD"
 * @param startTime - Heure de début locale au format "HH:mm"
 * @param endTime - Heure de fin locale au format "HH:mm" (optionnel)
 * @param timezone - IANA timezone de l'événement
 * @param startUTC - Date UTC ISO du début (pour le fallback +2h)
 * @returns Date ISO 8601 UTC
 */
export function localEndTimeToUTC(
  dateStr: string,
  startTime: string,
  endTime: string | undefined,
  timezone: string,
  startUTC: string
): string {
  if (!endTime || endTime.trim() === '') {
    // Pas d'endTime → start + 2 heures
    const fallback = new Date(startUTC);
    fallback.setTime(fallback.getTime() + 2 * 60 * 60 * 1000);
    return fallback.toISOString();
  }

  // Normaliser endTime: si c'est un format ISO "YYYY-MM-DDTHH:mm", extraire juste "HH:mm"
  let normalizedEndTime = endTime;
  if (endTime.includes('T')) {
    normalizedEndTime = endTime.split('T')[1].substring(0, 5);
  }

  // Déterminer si l'heure de fin dépasse minuit
  const startParts = startTime.split(':').map(Number);
  const endParts = normalizedEndTime.split(':').map(Number);
  const startMin = startParts[0] * 60 + (startParts[1] || 0);
  const endMin = endParts[0] * 60 + (endParts[1] || 0);

  let endDateStr = dateStr;
  if (endMin <= startMin) {
    // L'heure de fin est avant l'heure de début → c'est le lendemain
    const nextDay = new Date(dateStr + 'T00:00:00');
    nextDay.setDate(nextDay.getDate() + 1);
    endDateStr = nextDay.toISOString().split('T')[0];
  }

  return localToUTC(endDateStr, normalizedEndTime, timezone);
}

